// biome-ignore lint/style/useImportType: keep top-level `import type` (not inline) so Vite fully elides it and does not bundle the api router runtime into the client
import type { StatusLine } from '@langmail/api/trpc'

export type Tone = 'ok' | 'attn'

interface StatusPart {
  text: string
  tone?: Tone
}

// One `·`-separated segment of the status line, split into parts so a check
// mark or a warning can carry its own colour.
export type StatusSegment = StatusPart[]

const timeFormat = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' })
const weekdayFormat = new Intl.DateTimeFormat('en-US', { weekday: 'short' })
const monthFormat = new Intl.DateTimeFormat('en-US', { month: 'short' })

// "Tue 29 Sep", the mockup's day label, which no single locale formats as is.
const formatDay = (date: Date) => `${weekdayFormat.format(date)} ${date.getDate()} ${monthFormat.format(date)}`

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

// A time for today, a day otherwise, so a sync or brief that stalled days ago
// does not pass for this morning's.
const formatWhen = (date: Date, now: Date) => (isSameDay(date, now) ? timeFormat.format(date) : formatDay(date))

function mailboxSegment({ mailbox, lastSyncAt }: StatusLine, now: Date): StatusSegment {
  switch (mailbox.status) {
    case 'active':
      return [
        { text: 'mailbox ' },
        { text: '✓', tone: 'ok' },
        { text: lastSyncAt ? ` synced ${formatWhen(new Date(lastSyncAt), now)}` : ' live' },
      ]
    case 'pending':
      return [{ text: 'mailbox ' }, { text: 'setting up', tone: 'attn' }]
    case 'failed':
      return [{ text: 'mailbox ' }, { text: 'setup failed', tone: 'attn' }]
    default:
      return [{ text: 'mailbox ' }, { text: 'not set up', tone: 'attn' }]
  }
}

function briefSegment(lastBriefSentAt: string, now: Date): StatusSegment {
  const sentAt = new Date(lastBriefSentAt)
  if (isSameDay(sentAt, now)) {
    return [{ text: `brief sent ${timeFormat.format(sentAt)} ` }, { text: '✓', tone: 'ok' }]
  }
  return [{ text: `last brief ${formatDay(sentAt)}` }]
}

// The status line as segments, in the mockup's order: mailbox, agents, sends on
// hold, brief. Segments with nothing to say are left out rather than shown as
// zero, except the agent count, which is the line's reason to exist.
export function statusLineSegments(facts: StatusLine, now: Date): StatusSegment[] {
  const segments: StatusSegment[] = [mailboxSegment(facts, now), [{ text: plural(facts.agentCount, 'agent') }]]
  if (facts.sendsOnHold > 0) {
    segments.push([{ text: `${plural(facts.sendsOnHold, 'send')} on hold`, tone: 'attn' }])
  }
  if (facts.lastBriefSentAt) {
    segments.push(briefSegment(facts.lastBriefSentAt, now))
  }
  return segments
}
